import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Header from "./Header";
import InteractiveLines from "./InteractiveLines-i";
import Footer from "./Footer";

const Infosafe: React.FC = () => {
  const { scrollY } = useScroll();
  const scrollX = useTransform(scrollY, [0, 400], [0, -200]);
  const xSpring = useSpring(scrollX, { stiffness: 100, damping: 30 });
  const heroRef = useRef(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const scrollToProjects = () => {
    window.location.href = '/all-projects';
  };

  useEffect(() => {
    console.log('Infosafe component mounted');
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let isHovering = false;
    let mouseX = 0;
    let mouseY = 0;
    let points: { x: number; y: number }[] = [];
    const maxPoints = 20;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
      
      // Add new point
      points.push({ x: mouseX, y: mouseY });
      
      // Keep only the last maxPoints points
      if (points.length > maxPoints) {
        points.shift();
      }
    };

    const handleMouseEnter = () => {
      isHovering = true;
      points = [];
    };

    const handleMouseLeave = () => {
      isHovering = false;
      points = [];
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const drawGlowingLine = () => {
      if (!isHovering || points.length < 2) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw the main line
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.strokeStyle = '#FFB800';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw glow effect
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.strokeStyle = '#FFB800';
      ctx.shadowColor = '#FFB800';
      ctx.shadowBlur = 15;
      ctx.lineWidth = 1;
      ctx.stroke();

      requestAnimationFrame(drawGlowingLine);
    };

    drawGlowingLine();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-[#F4F1EC]">
      <Header />
      <motion.section 
        ref={heroRef}
        className="relative w-full min-h-[100vh] h-screen overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <InteractiveLines />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative z-20 translate-y-50"
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <motion.img 
              src="/card2.png" 
              alt="Infosafe" 
              className="w-72 h-auto"
              whileHover={{
                scale: 1.1,
                transition: {
                  duration: 0.3,
                  ease: "easeOut"
                }
              }}
              onError={(e) => {
                console.error('Error loading image:', e);
                e.currentTarget.src = '/placeholder.png';
              }}
            />
          </motion.div>
        </div>
        <motion.h1
          className="relative z-10 text-[40vw] md:text-[35vw] lg:text-[30vw] font-serif font-bold text-right mt-[45vh] md:mt-[25vh] pr-[5vw] whitespace-nowrap"
          style={{ x: xSpring }}
        >
          INFOSAFE
        </motion.h1>
        <div className="absolute bottom-0 left-0 right-0 h-[30vh] bg-gradient-to-t from-[#F4F1EC] to-transparent z-40" />
      </motion.section>
      
      {/* Gradient Section */}
      <motion.section 
        className="relative w-full h-[600px] bg-gradient-to-b from-[#FFB800] via-[#F4F1EC] via-[#F4F1EC] via-[#F4F1EC] via-[#F4F1EC] to-[#F4F1EC] py-10 px-4 md:px-8 flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        {/* Mouse drawing effect */}
        <div 
          className="absolute inset-0 cursor-crosshair z-30 max-w-[90%] md:max-w-none mx-auto"
          onMouseMove={(e) => {
            const lastSquare = e.currentTarget.lastElementChild;
            if (lastSquare) {
              const lastRect = lastSquare.getBoundingClientRect();
              const distance = Math.sqrt(
                Math.pow(e.clientX - lastRect.left, 2) + 
                Math.pow(e.clientY - lastRect.top, 2)
              );

              // Only create new square if distance is greater than 20px
              if (distance < 20) return;
            }

            const square = document.createElement('div');
            square.className = 'absolute w-2 h-2 bg-[#FFB800] z-30';
            square.style.left = `${e.clientX}px`;
            square.style.top = `${e.clientY}px`;
            square.style.boxShadow = '0 0 10px #FFB800, 0 0 20px #FFB800';
            square.style.opacity = '0.8';
            e.currentTarget.appendChild(square);

            // Remove square after 2 seconds
            setTimeout(() => {
              square.remove();
            }, 2000);
          }}
        />

        <div className="absolute top-10 w-full overflow-hidden z-20">
          <motion.div 
            className="whitespace-nowrap text-4xl font-light italic font-inter text-black"
            initial={{ x: "100%" }}
            animate={{ x: "-100%" }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            Web Design and Development
          </motion.div>
        </div>
        
        <div className="relative flex items-center justify-center z-10">
          <h1 className="text-[120px] font-semibold italic font-inter">Web Design and Development</h1>
        </div>
      </motion.section>

      {/* Overview Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-12 mb-16 border-b border-gray-300 pb-10">
            <div>
              <p className="text-xs uppercase tracking-widest mb-1 font-inter text-gray-500">Role</p>
              <p className="text-black font-medium font-inter">UI/UX Designer · Web Designer · Engineer</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest mb-1 font-inter text-gray-500">Type</p>
              <p className="text-black font-medium font-inter">Web Application</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h2 className="text-5xl font-bold font-serif">About</h2>
              <p className="text-lg text-gray-700 leading-relaxed font-inter">
                InfoSafe is a real-time community safety platform that allows users to report, share, and track local incidents. The goal was to lower the barrier for ordinary people to flag safety concerns, making the process fast, clear, and accessible.
              </p>
              <div className="bg-[#FFB800] p-6 rounded-lg">
                <p className="font-medium font-inter">
                  Goal: Give communities a simple, reliable way to stay informed and respond to safety events in real time.
                </p>
              </div>
            </div>
            <div>
              <img src="/info.png" alt="IN4SAFE" className="w-full rounded-lg shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="w-full bg-[#111] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif text-white mb-10">The Problem</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", title: "No central place to report", body: "Community members had no unified platform to flag local incidents. Information was scattered across social media and group chats." },
              { n: "02", title: "Slow response time", body: "Without real-time visibility, safety managers and organizations couldn't act quickly enough when incidents occurred." },
              { n: "03", title: "Lack of trust in the process", body: "People were unsure whether their reports were received or acted on. Transparency was missing from end to end." },
            ].map((item) => (
              <div key={item.n} className="border border-[#FFB800] p-6 rounded-lg">
                <p className="text-[#FFB800] text-2xl font-bold mb-3 font-inter">{item.n}</p>
                <p className="text-white font-medium mb-2 font-inter">{item.title}</p>
                <p className="text-gray-400 text-sm leading-relaxed font-inter">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Users Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif text-black mb-16">Target Users</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { src: "/info-user1.png", alt: "Community residents", title: "Community Residents", desc: "Everyday users who want a fast, simple way to flag safety concerns in their neighbourhood without navigating complicated systems or waiting on hold." },
              { src: "/info-user2.png", alt: "Community managers", title: "Community Managers", desc: "People responsible for maintaining safety across shared spaces like apartment complexes, campuses, and local districts who need real-time visibility to act quickly." },
              { src: "/info-user3.png", alt: "Public safety organizations", title: "Public Safety Organizations", desc: "Institutions and response teams that rely on accurate, timely incident data to coordinate resources and keep communities informed." },
            ].map((user) => (
              <div key={user.title} className="flex flex-col gap-4">
                <img src={user.src} alt={user.alt} className="w-full rounded-lg" />
                <div>
                  <p className="font-serif text-xl font-semibold text-black mb-2">{user.title}</p>
                  <p className="font-inter text-base text-gray-600 leading-relaxed">{user.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif text-black mb-16">Main Features</h2>
          <div className="flex flex-col divide-y divide-gray-200">
            {[
              { feature: "Incident Report", desc: "Allow users to create, edit, and delete incident reports with real-time status updates, location tagging, and photo attachments, making it easy to document and share safety concerns the moment they occur." },
              { feature: "Real-Time Alert", desc: "Notify users in the vicinity of reported incidents through push notifications and in-app alerts, ensuring communities stay informed and can take action before situations escalate." },
              { feature: "User Accounts", desc: "Registration and login using secure authentication, giving each user a personal profile to manage their reports, track incident history, and customize alert preferences." },
              { feature: "News Report", desc: "Aggregates the latest news and updates about local incidents, providing users with verified information and reducing the spread of misinformation within the community." },
            ].map((item) => (
              <div key={item.feature} className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-6 py-8">
                <p className="font-serif text-2xl font-semibold text-black border-l-4 border-[#FFB800] pl-4 flex items-center">{item.feature}</p>
                <p className="font-inter text-lg text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Flow Section */}
      <section className="w-full bg-black py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="h-1 w-[500px] bg-[#FFB800] rounded mb-20"></div>
          <div className="grid grid-cols-1 md:grid-cols-[45%_55%] gap-10">
            <div className="text-white">
              <h2 className="text-7xl font-serif mb-8">User Flow</h2>
              <div className="space-y-5 text-gray-300 font-inter text-lg leading-relaxed">
                <p>The user flow maps the core journey from first contact to active use, covering authentication, incident reporting, alert browsing, and account management.</p>
                <p>New users land on the login page and can register or sign in. Once authenticated, the dashboard becomes the home base: a live map showing nearby incidents. From here, users can add a new alert, browse the alerts feed, read the latest news, or adjust their profile settings.</p>
                <p>Every path is designed to be short. The most critical action, reporting an incident, is always one tap away via the Add button in the navigation bar.</p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <img src="/info-flow.png" alt="User Flow Diagram" className="w-2/3" />
            </div>
          </div>
        </div>
      </section>

      {/* Wireframe Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif text-black mb-3">Wireframes</h2>
          <p className="text-xs uppercase tracking-widest text-gray-400 font-inter mb-16">Early-stage layout exploration. Structure and flow defined before visual styling.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { n: 1, label: "Landing / Login" },
              { n: 2, label: "Dashboard" },
              { n: 3, label: "Add Alert" },
              { n: 4, label: "Incident Detail" },
              { n: 5, label: "Alerts" },
              { n: 6, label: "News" },
            ].map(({ n, label }) => (
              <div key={n} className="flex flex-col gap-2">
                <img src={`/info-wf-${n}.png`} alt={label} className="w-full h-auto rounded-lg" />
                <p className="text-center text-sm font-inter text-gray-500 tracking-widest uppercase">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interface Design Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif mb-3">Interface Design</h2>
          <p className="text-xs uppercase tracking-widest text-gray-400 font-inter mb-12">Visual language built around urgency, clarity, and trust</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed font-inter">
                The interface was designed around speed and clarity. Yellow was chosen as the primary accent: high-visibility, urgency-coded, yet approachable. Every screen prioritizes the reporting action above all else, reducing steps to get critical information submitted.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-inter">
                The login portal keeps friction minimal so users can authenticate quickly and move directly into reporting or browsing community alerts.
              </p>
            </div>
            <div>
              <img src="/info-1.png" alt="IN4SAFE Login Portal" className="w-full rounded-lg shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* High-Fidelity Designs Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-serif text-black mb-3">High-Fidelity Designs</h2>
          <p className="text-xs uppercase tracking-widest text-gray-400 font-inter mb-16">Final screens with full visual design applied. Color, typography, and interaction ready for development.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { n: 1, label: "Landing / Login" },
              { n: 2, label: "Sign Up" },
              { n: 3, label: "Dashboard" },
              { n: 4, label: "Add Alert" },
              { n: 5, label: "Alerts" },
              { n: 6, label: "News" },
              { n: 7, label: "Profile" },
              { n: 8, label: "Settings" },
            ].map(({ n, label }) => (
              <div key={n} className="flex flex-col gap-2">
                <img src={`/info-${n}.png`} alt={label} className="w-full h-auto rounded-lg" />
                <p className="text-center text-sm font-inter text-gray-500 tracking-widest uppercase">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables & Future Section */}
      <section className="w-full bg-[#F4F1EC] py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="border-t border-gray-300 pt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
            <h2 className="text-5xl font-serif">Deliverables & Future</h2>
            <div className="space-y-6 text-gray-700 text-lg leading-relaxed font-inter">
              <p>
                The project delivered a full set of high-fidelity screens covering the landing page, dashboard with interactive map, alerts page, incident editing, and news feed, each designed to work together as a cohesive product experience.
              </p>
              <p>
                Looking ahead, the MVP would focus on authentication and basic incident reporting. A beta phase would introduce real-time alerts, anonymous reporting, and filtering. A full launch would bring admin tools, enhanced security, and scalability improvements to support a growing user base.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* View All Projects Button */}
      <div className="w-full bg-[#F4F1EC] py-12 pb-24 flex justify-center">
        <button 
          onClick={scrollToProjects}
          className="bg-[#FFB800] text-black border border-black rounded-full px-10 py-4 font-serif text-base font-medium transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-[0_8px_0_0_#000000] active:translate-y-0 active:shadow-[0_1px_0_0_#000000]"
        >
          VIEW ALL PROJECTS
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default Infosafe; 